import { BookingType } from "../../../Booking-Form/typings/booking-inputs";
import type {
  IndependentBookingMode,
  IndependentBookingSetting
} from "../../../Login/typings/login.types";
import {
  BOOKING_TYPE_ENABLING_ROWS,
  BookingTypeEnablingRowConfig,
  BookingTypeEnablingVisibilityContext
} from "./company-card-booking-type-enabling.config";

export function defaultIndependentBookingMode(bookingType: BookingType): IndependentBookingMode {
  return bookingType === BookingType.VEHICLE ? "forbidden" : "admins_only";
}

export function normalizeIndependentBookingSettings(
  fromApi: IndependentBookingSetting[] | undefined,
  visibleTypes: BookingType[]
): IndependentBookingSetting[] {
  return visibleTypes.map(bookingType => {
    const existing = fromApi?.find(s => s.bookingType === bookingType);
    return existing ?? { bookingType, mode: defaultIndependentBookingMode(bookingType) };
  });
}

export function getVisibleIndependentBookingRows(
  ctx: BookingTypeEnablingVisibilityContext
): BookingTypeEnablingRowConfig[] {
  return BOOKING_TYPE_ENABLING_ROWS.filter(
    row => row.isVisible(ctx) && row.bookingType != null && row.bookingType !== BookingType.VISITOR
  );
}

export function getVisibleIndependentBookingTypes(
  ctx: BookingTypeEnablingVisibilityContext
): BookingType[] {
  return getVisibleIndependentBookingRows(ctx).flatMap(row =>
    row.bookingType != null ? [row.bookingType] : []
  );
}

export function buildIndependentBookingSettingsPayload(
  settings: IndependentBookingSetting[] | undefined,
  visibleTypes: BookingType[]
): IndependentBookingSetting[] {
  return normalizeIndependentBookingSettings(settings, visibleTypes);
}

export function updateIndependentBookingMode(
  settings: IndependentBookingSetting[] | undefined,
  bookingType: BookingType,
  mode: IndependentBookingMode,
  visibleTypes: BookingType[]
): IndependentBookingSetting[] {
  const normalized = normalizeIndependentBookingSettings(settings, visibleTypes);
  return normalized.map(setting =>
    setting.bookingType === bookingType ? { ...setting, mode } : setting
  );
}

export const INDEPENDENT_BOOKING_MODE_OPTIONS: readonly {
  mode: IndependentBookingMode;
  labelKey: string;
  testIdSuffix: string;
}[] = [
  {
    mode: "forbidden",
    labelKey: "_companyCard_independentBookings_mode_forbidden",
    testIdSuffix: "forbidden"
  },
  {
    mode: "admins_only",
    labelKey: "_companyCard_independentBookings_mode_admins",
    testIdSuffix: "admins"
  },
  {
    mode: "everyone",
    labelKey: "_companyCard_independentBookings_mode_everyone",
    testIdSuffix: "everyone"
  }
];

export const INDEPENDENT_BOOKING_TYPE_ROW_IDS: Partial<Record<BookingType, string>> = {
  [BookingType.WORKPLACE]: "workplace",
  [BookingType.PARKINGPLACE]: "parkingplace",
  [BookingType.ELECTRICCHARGINGSTATIONPLACE]: "electricchargingplace",
  [BookingType.VEHICLE]: "vehicle",
  [BookingType.CONFERENCEZONE]: "conferencezone"
};

/** Title keys aligned with advanced booking / check-in policy accordions */
export const INDEPENDENT_BOOKING_TYPE_TITLE_KEYS: Partial<Record<BookingType, string>> = {
  [BookingType.WORKPLACE]: "Workplace",
  [BookingType.PARKINGPLACE]: "Parking",
  [BookingType.ELECTRICCHARGINGSTATIONPLACE]: "Electric Charging",
  [BookingType.VEHICLE]: "_bookingTypeVehicle",
  [BookingType.CONFERENCEZONE]: "_bookTypeMeetingRoom"
};

export function getIndependentBookingModeLabelKey(mode: IndependentBookingMode): string {
  return (
    INDEPENDENT_BOOKING_MODE_OPTIONS.find(option => option.mode === mode)?.labelKey ??
    INDEPENDENT_BOOKING_MODE_OPTIONS[0].labelKey
  );
}
